import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../../../Translation";
import { ArrowDownIcon, ArrowsPointingOutIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { webhook } from "../../../../../Resources/Images";
import { TextInput } from "../../../../Inputs/TextInput";
import { TextArea } from "../../../../Inputs/TextArea";
import { Button } from "../../../../Buttons";
import Tabs, { useTabs } from "../../../../Tabs";
import { useAPI } from "../../../../../API";
import Select from "../../../../Inputs/Select";
import { bgColor, hoverBgColor } from "../../../../../Resources/CSS/styling";
import Checkbox from "../../../../Inputs/Checkbox";
const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate, userApp, appWebhookStep }) => {

    const { translate } = useTranslation();
    const api = useAPI();

    const [prompt, setPrompt] = useState(tmpStep.data.prompt);
    const [model, setModel] = useState(tmpStep.data.model);
    const [returnBooleanOnly, setReturnBooleanOnly] = useState(tmpStep.data.return_boolean_only);
    const [addContactContext, setAddContactContext] = useState(tmpStep.data.add_contact_context);

    const onDataChange = (prompt, model, returnBooleanOnly, addContactContext) => {

        const promptWithReturnBooleanOnly = returnBooleanOnly ? `${prompt}\n\nReturn a boolean value only, no other text or explanation. The answer should be "true" or "false". The answer should be in the following format: "true" or "false".` : prompt;

        const body = {
            "model": model,
            "messages": [{"role": "user", "content": promptWithReturnBooleanOnly}]
          }

        if (model && !model.includes('5')) {
            body.temperature = 0.7;
        }

        const APICallStepData = {
            method: 'POST',
            url: 'https://api.openai.com/v1/chat/completions',
            body: body,
            headers: {
                'Authorization': 'Bearer ' + userApp.data.api_key
            },
            extract_variables: [
                {
                    from: "choices.message.content",
                    to: "event.chatgpt_result"
                }
            ],
            prompt: prompt,
            model: model,
            return_boolean_only: returnBooleanOnly,
            add_contact_context: addContactContext,
            chatgpt_add_contact_context: addContactContext ? true : false
        }

        setTmpStep({ ...tmpStep, app_webhook_unique_identifier: appWebhookStep.webhook_unique_identifier, user_app_id: userApp.id, data: APICallStepData });
    }

    const models = useMemo(() => {
        return [
            { label: 'gpt-4-turbo', value: 'gpt-4-turbo' },
            { label: 'gpt-4o', value: 'gpt-4o' },
            { label: 'gpt-5', value: 'gpt-5' },
            { label: 'gpt-5-mini', value: 'gpt-5-mini' },
            { label: 'gpt-5-nano', value: 'gpt-5-nano' },

        ]
    }, []);

    const modelSelected = models.find(m => m.value === model);

    return (
        <div className="flex flex-col mt-4 overflow-visible mb-16">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}
            <p className="text-md text-gray-600 font-bold">{translate('automatisation.chatgpt_result_explication')}</p>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.prompt')}</p>
                <TextArea value={prompt} onChange={(e) => {setPrompt(e.target.value); onDataChange(e.target.value, model, returnBooleanOnly, addContactContext)}} />
            </div>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.model')}</p>
                <Select options={models} selected={modelSelected} setSelected={(e) => {setModel(e.value); onDataChange(prompt, e.value, returnBooleanOnly, addContactContext)}} />
            </div>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.return_boolean_only')}</p>
                <p className="text-sm text-gray-600 font-normal">{translate('automatisation.return_boolean_only_description')}</p>
                <Checkbox checked={returnBooleanOnly} onChange={(e) => {setReturnBooleanOnly(e.target.checked); onDataChange(prompt, model, e.target.checked, addContactContext)}} />
            </div>
            <div className="flex flex-col mt-4">
                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.add_contact_context')}</p>
                <p className="text-sm text-gray-600 font-normal">{translate('automatisation.add_contact_context_description')}</p>
                <Checkbox checked={addContactContext} onChange={(e) => {setAddContactContext(e.target.checked); onDataChange(prompt, model, returnBooleanOnly, e.target.checked)}} />
            </div>
            
            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function ChatGPTPromptPanel({ setStepToClone, panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep, app, appWebhookStep, userApp }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);

    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {

            const step = {
                ...tmpStep,
                data: {
                    ...tmpStep.data,
                    headers: tmpStep.data.headers,
                    url: tmpStep.data.url,
                    method: tmpStep.data.method,
                    body: tmpStep.data.body
                }
            }

            if (isCreate) {
                try {
                    if (!step.description || step.description.trim() === '') {
                        step.description = "Send prompt to ChatGPT";
                    }

                    const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, step);
                    if (newStepResponse?.data?.automatisation_step) {
                        await onCreateNewStep(newStepResponse.data.automatisation_step);
                    }
                } catch (e) {}
                
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, step);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }

    const selectStepToClone = (step) => {
        setStepToClone(step);
        panelHook.setOpen(false);
    }


    return (
        <div className="flex flex-col flex-1 h-min rounded-md p-4 bg-white overflow-hidden">
            <div className="flex flex-row items-center">
                <img src={app?.icon} alt="webhook" className="w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md mr-2 text-black" />
                <h2 className="text-xl font-bold">{app?.name}</h2>
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                {!isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => selectStepToClone(tmpStep)}>{translate('clone')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} userApp={userApp} appWebhookStep={appWebhookStep} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}